import React, { useState, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { AppContext } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { 
  RotateCcw, Phone, MapPin, Euro, Check, 
  ArrowRight, Shield, Clock, Calendar
} from 'lucide-react';

export default function LouerMateriel() {
  const { i18n } = useTranslation();
  const isFr = i18n.language === 'fr';
  const { API } = useContext(AppContext);
  
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    nom: '',
    telephone: '',
    email: '',
    type_materiel: '',
    localisation: '',
    tarif_propose: '',
    periode_disponibilite: '',
    rgpd_accepted: false,
  });

  const typesMateriels = [
    { value: 'foreuse', label: isFr ? 'Foreuse géotechnique' : 'Geotechnical drill rig' },
    { value: 'pompe', label: isFr ? 'Pompe / Centrale hydraulique' : 'Pump / Hydraulic unit' },
    { value: 'compresseur', label: isFr ? 'Compresseur' : 'Compressor' },
    { value: 'instrumentation', label: isFr ? 'Instrumentation' : 'Instrumentation' },
    { value: 'penetrometre', label: isFr ? 'Pénétromètre (SPT/CPT)' : 'Penetrometer (SPT/CPT)' },
    { value: 'transport', label: isFr ? 'Engin de transport' : 'Transport equipment' },
    { value: 'autre', label: isFr ? 'Autre matériel' : 'Other equipment' },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.rgpd_accepted) {
      toast.error(isFr ? 'Veuillez accepter les conditions' : 'Please accept the conditions');
      return;
    }
    setLoading(true);
    try {
      const formDataToSend = new FormData();
      Object.keys(formData).forEach(key => {
        if (formData[key] !== '' && formData[key] !== null) {
          formDataToSend.append(key, formData[key]);
        }
      });
      await axios.post(`${API}/demandes/location`, formDataToSend);
      setSubmitted(true);
      toast.success(isFr ? 'Demande envoyée !' : 'Request sent!');
    } catch (error) {
      toast.error(isFr ? 'Erreur lors de l\'envoi' : 'Error sending request');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center px-4">
        <div className="max-w-md text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Check className="w-10 h-10 text-green-600" />
          </div>
          <h1 className="text-3xl font-bold text-[#0A2540] mb-4">{isFr ? 'Merci !' : 'Thank you!'}</h1>
          <p className="text-gray-600 mb-6">
            {isFr ? 'Votre proposition de location a été reçue. Nous vous rappelons sous 24h.' : 'Your rental offer has been received. We\'ll call you within 24h.'}
          </p>
          <div className="flex items-center justify-center gap-2 text-[#635BFF] font-medium">
            <Phone className="w-5 h-5" />
            <span>+33 7 82 93 93 83</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <section className="pt-16 pb-8 px-4">
        <div className="container mx-auto max-w-4xl text-center">
          <div className="inline-flex items-center gap-2 bg-purple-100 rounded-full px-4 py-2 mb-6">
            <RotateCcw className="w-4 h-4 text-purple-600" />
            <span className="text-sm font-medium text-purple-600">{isFr ? 'Location de matériel' : 'Equipment rental'}</span>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-[#0A2540] mb-4">
            {isFr ? 'Louez votre matériel géotechnique' : 'Rent out your geotechnical equipment'}
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            {isFr ? 'Rentabilisez votre matériel inactif. On gère les locataires, la logistique et les paiements.' : 'Monetize your idle equipment. We handle tenants, logistics and payments.'}
          </p>
        </div>
      </section>

      <section className="pb-8 px-4">
        <div className="container mx-auto max-w-4xl">
          <div className="flex flex-wrap justify-center gap-6 text-sm text-gray-600">
            <div className="flex items-center gap-2"><Shield className="w-4 h-4 text-green-500" />{isFr ? 'Assurance incluse' : 'Insurance included'}</div>
            <div className="flex items-center gap-2"><Clock className="w-4 h-4 text-blue-500" />{isFr ? 'Réponse sous 24h' : 'Response within 24h'}</div>
            <div className="flex items-center gap-2"><Euro className="w-4 h-4 text-amber-500" />{isFr ? 'Frais fixe + 5% (plafond 500€)' : 'Fixed fee + 5% (max €500)'}</div>
          </div>
        </div>
      </section>

      <section className="pb-16 px-4">
        <div className="container mx-auto max-w-2xl">
          <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-xl p-6 md:p-8 border border-gray-100">
            <div className="space-y-6">
              <div>
                <Label htmlFor="nom">{isFr ? 'Nom complet' : 'Full name'} *</Label>
                <Input id="nom" required value={formData.nom} onChange={(e) => setFormData({ ...formData, nom: e.target.value })} className="mt-2" />
              </div>
              <div>
                <Label htmlFor="telephone">{isFr ? 'Téléphone' : 'Phone'} *</Label>
                <Input id="telephone" type="tel" required value={formData.telephone} onChange={(e) => setFormData({ ...formData, telephone: e.target.value })} className="mt-2" />
              </div>
              <div>
                <Label htmlFor="email">Email <span className="text-gray-400">({isFr ? 'optionnel' : 'optional'})</span></Label>
                <Input id="email" type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} className="mt-2" />
              </div>
              <div>
                <Label>{isFr ? 'Type de matériel' : 'Equipment type'} *</Label>
                <Select value={formData.type_materiel} onValueChange={(value) => setFormData({ ...formData, type_materiel: value })}>
                  <SelectTrigger className="mt-2"><SelectValue placeholder={isFr ? 'Sélectionnez' : 'Select'} /></SelectTrigger>
                  <SelectContent>
                    {typesMateriels.map((type) => (<SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="localisation">{isFr ? 'Localisation' : 'Location'} *</Label>
                <Input id="localisation" required value={formData.localisation} onChange={(e) => setFormData({ ...formData, localisation: e.target.value })} placeholder="France, 69001" className="mt-2" />
              </div>
              <div>
                <Label htmlFor="tarif">{isFr ? 'Tarif proposé (€/jour ou €/mois)' : 'Proposed rate'}</Label>
                <Input id="tarif" value={formData.tarif_propose} onChange={(e) => setFormData({ ...formData, tarif_propose: e.target.value })} placeholder="500€/jour" className="mt-2" />
              </div>
              <div>
                <Label htmlFor="periode">{isFr ? 'Période de disponibilité' : 'Availability period'}</Label>
                <Input id="periode" value={formData.periode_disponibilite} onChange={(e) => setFormData({ ...formData, periode_disponibilite: e.target.value })} placeholder={isFr ? 'Ex: Juin à Décembre 2025' : 'Ex: June to December 2025'} className="mt-2" />
              </div>
              <div className="pt-4 border-t">
                <div className="flex items-start gap-3">
                  <Checkbox id="rgpd" checked={formData.rgpd_accepted} onCheckedChange={(checked) => setFormData({ ...formData, rgpd_accepted: checked })} />
                  <Label htmlFor="rgpd" className="text-sm text-gray-600 cursor-pointer">
                    {isFr ? "J'accepte les conditions de mise en relation et le traitement de mes données (RGPD)" : "I accept the terms and data processing (GDPR)"}
                  </Label>
                </div>
              </div>
              <Button type="submit" disabled={loading || !formData.rgpd_accepted} className="w-full bg-purple-600 hover:bg-purple-700 text-white py-4 text-lg">
                {loading ? <span className="flex items-center justify-center gap-2"><div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>{isFr ? 'Envoi...' : 'Sending...'}</span> : <span className="flex items-center justify-center gap-2">{isFr ? 'Proposer ma location' : 'Submit my rental'}<ArrowRight className="w-5 h-5" /></span>}
              </Button>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
}
